import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import "@splidejs/react-splide/css";
import { addDataLayer } from "@/helpers/dataLayer";
import { sendSalesforceInteraction } from "@/helpers/cdnSdk";
import { useAppSelector } from "@/utils/hooks/reactUseCustomHooks";
import { getUTMParameter } from "@/helpers/getUTMParameters";
// import { is } from "date-fns/locale";

interface SplideSliderProps {
  slidesPerView?: {
    base?: number;
    xsm?: number;
    sm?: number;
    ssm?: number;
    md?: number;
    smd?: number;
    lg?: number;
    xl?: number;
    xml?: number;
    xxl?: number;
  };
  slidesToScroll?: {
    base?: number;
    xsm?: number;
    sm?: number;
    ssm?: number;
    md?: number;
    smd?: number;
    lg?: number;
    xl?: number;
    xml?: number;
    xxl?: number;
  };
  options?: any;
  showControls?: boolean;
  slideClassName?: string;
  isShowArrow?: boolean;
  isCenterArrow?: boolean;
  bannerKey?: boolean;
  children: React.ReactNode;

  // New props for custom classes
  className?: string;
  wrapperClassName?: string;
  rowClassName?: string;
  viewportClassName?: string;
  containerClassName?: string;
  controlsClassName?: string;
  buttonsClassName?: string;
  paginationClassName?: string;
  arrowClassName?: string;
  leftArrowClassName?: string;
  rightArrowClassName?: string;
  CenterArrowClassName?: string;
  carouselData?: any;
}

// Utility to generate Splide breakpoints from props
const getSplideBreakpoints = (
  slidesPerView?: SplideSliderProps["slidesPerView"],
  slidesToScroll?: SplideSliderProps["slidesToScroll"]
) => {
  const breakpoints: Record<number, any> = {};
  if (!slidesPerView && !slidesToScroll) return breakpoints;
  if (slidesPerView?.xxl || slidesToScroll?.xxl)
    breakpoints[1920] = {
      perPage: slidesPerView?.xxl,
      perMove: slidesToScroll?.xxl,
    };
  if (slidesPerView?.xml || slidesToScroll?.xml)
    breakpoints[1401] = {
      perPage: slidesPerView?.xml,
      perMove: slidesToScroll?.xml,
    };
  if (slidesPerView?.xl || slidesToScroll?.xl)
    breakpoints[1280] = {
      perPage: slidesPerView?.xl,
      perMove: slidesToScroll?.xl,
    };
  if (slidesPerView?.lg || slidesToScroll?.lg)
    breakpoints[1100] = {
      perPage: slidesPerView?.lg,
      perMove: slidesToScroll?.lg,
    };
  if (slidesPerView?.md || slidesToScroll?.md)
    breakpoints[1023] = {
      perPage: slidesPerView?.md,
      perMove: slidesToScroll?.md,
    };
  if (slidesPerView?.smd || slidesToScroll?.smd)
    breakpoints[850] = {
      perPage: slidesPerView?.smd,
      perMove: slidesToScroll?.smd,
    };
  if (slidesPerView?.ssm || slidesToScroll?.ssm)
    breakpoints[768] = {
      perPage: slidesPerView?.ssm,
      perMove: slidesToScroll?.ssm,
    };
  if (slidesPerView?.sm || slidesToScroll?.sm)
    breakpoints[576] = {
      perPage: slidesPerView?.sm,
      perMove: slidesToScroll?.sm,
    };
  if (slidesPerView?.xsm || slidesToScroll?.xsm)
    breakpoints[410] = {
      perPage: slidesPerView?.xsm,
      perMove: slidesToScroll?.xsm,
    };
  return breakpoints;
};

// Arrow nav 
export const Arrow = React.memo(
  ({
    direction,
    className,
    disabled,
    ...props
  }: {
    className?: string;
    disabled?: boolean;
    direction: "prev" | "next";
  } & React.ButtonHTMLAttributes<HTMLButtonElement>) => (
    <button
      type="button"
      aria-label={direction === "prev" ? "Previous Button" : "Next Button"}
      className={`slider_button slider_button--${direction} ${disabled && "!bg-[--grey300] !border-[--grey300] !text-[#777] !cursor-not-allowed"} border rounded-full cursor-pointer w-[40px] h-[40px] flex items-center justify-center ${className}`}
      disabled={disabled}
      {...props}
    >
      <span
        className={`text-brandBlack ${direction === "prev" ? "icon-prev-arrow" : "icon-next-arrow"} !text-sm`}
      ></span>
    </button>
  )
);

// pagination Dots

const Pagination = React.memo(
  ({
    slideCount,
    slidesToScroll,
    selected,
    className,
    onClick,
  }: {
    slideCount: number;
    slidesToScroll: number;
    selected: number;
    className?: string;
    onClick: (idx: number) => void;
  }) => {
    const dotCount = Math.ceil(slideCount / slidesToScroll);

    return (
      <div
        className={`slider_dots flex gap-[8px] items-center ${className || ""}`}
      >
        {Array.from({ length: dotCount }).map((_, idx) => (
          <button
            key={idx}
            type="button"
            aria-label={`slide ${idx}`}
            onClick={() => onClick(idx * slidesToScroll)}
            className={`slider_dot ${selected !== idx && "bg-grey300"} w-[16px] h-[16px] rounded-full transition-all duration-150 ease-in-out ${selected === idx ? "slider_dot--selected bg-brandSecondary w-[32px] h-[16px] rounded-[20px]" : ""}`}
          />
        ))}
      </div>
    );
  }
);

// splide slider 

const SplideSlider: React.FC<SplideSliderProps> = ({
  className = "",
  slidesPerView = { base: 1 },
  slidesToScroll = { base: 1 },
  options = {},
  showControls = true,
  isShowArrow = true,
  isCenterArrow = true,
  slideClassName = "slider_slide items-stretch tab",
  children,
  carouselData,
  bannerKey = false,

  // New class props
  wrapperClassName = "",
  rowClassName = "",
  viewportClassName = "",
  containerClassName = "",
  controlsClassName = "",
  buttonsClassName = "",
  paginationClassName = "",
  arrowClassName = "",
  leftArrowClassName = "",
  rightArrowClassName = "",
  CenterArrowClassName = "",
}) => {
  const [selected, setSelected] = useState(0);
  const [slideCount, setSlideCount] = useState(0);
  const splideRef = useRef<any>(null);
  const { uid: Uid, isLoading: isloading } = useAppSelector(
    (state) => state.gigyaslice
  );
  const viewedBannersRef = useRef<Set<number>>(new Set());
  const [isLoaded, setIsLoaded] = useState(false);

  const slides = useMemo(
    () =>
      React.Children.map(children, (child) => (
        <SplideSlide className={slideClassName}>{child}</SplideSlide>
      )),
    [children, slideClassName]
  );


  useEffect(() => {
    if (splideRef.current) {
      setSlideCount(splideRef.current.splide.length);
    }
  }, [children]);

  const onMove = useCallback((splide: any) => {
    setSelected(splide.index);
  }, []);

  // Add immediate state update when arrow is clicked
  const handlePrevClick = useCallback(() => {
    if (splideRef.current) {
      const splide = splideRef.current.splide;
      const newIndex = Math.max(0, selected - (slidesToScroll.base || 1));
      setSelected(newIndex); // Update immediately
      splide.go("<");
    }
  }, [selected, slidesToScroll.base]);

  const handleNextClick = useCallback(() => {
    if (splideRef.current) {
      const splide = splideRef.current.splide;
      const newIndex = Math.min(
        slideCount - 1,
        selected + (slidesToScroll.base || 1)
      );
      setSelected(newIndex); // Update immediately
      splide.go(">");
    }
  }, [selected, slidesToScroll.base, slideCount]);

  const splideOptions: any = useMemo(
    () => ({
      type: options.loop ? "loop" : "slide",
      perPage: slidesPerView.base,
      perMove: slidesToScroll.base,
      arrows: false,
      pagination: false,
      drag: options.drag !== false,
      autoplay: options.autoplay || false,
      interval: options.interval || 3000,
      lazyLoad: options.layLoad || false,
      ...options,
      breakpoints: getSplideBreakpoints(slidesPerView, slidesToScroll),
    }),
    [options, slidesPerView, slidesToScroll]
  );

  const shouldShowControls = showControls && slideCount > 1;

  // Fixed logic for determining if we're at the last position
  const currentSlidesToScroll = slidesToScroll.base || 1;
  const isAtStart = selected === 0;
  const isAtEnd = selected >= slideCount - currentSlidesToScroll;


  useEffect(() => {
    setTimeout(() => {
      setIsLoaded(true);
    }, 100);
    if (bannerKey) {
      try {
        if (!isloading) {
          if (!carouselData || carouselData.length == 0) return;
          // Trigger first view event only once for first banner
          const currentBanner = carouselData[selected];
          if (!currentBanner?.gamId) {  // if ad id is there then dl should not hit the event
            if (!viewedBannersRef?.current?.has(0)) {
              addDataLayer({
                event: "banner_view",
                click_text: currentBanner?.imagealt || "default",
                click_url: `${selected + 1}`,
                page_title: document.title,
              });
              sendSalesforceInteraction({
                name: "homepage_view",
                banner: process.env.NEXT_PUBLIC_MERCHANT_IDENTIFIER,
                isAnonymous: Uid ? 0 : 1,
                gigyaUid: Uid,
                text: currentBanner?.imagealt || "default",
                section: "banner",
                utmSource: getUTMParameter("utm_source"),
                utmMedium: getUTMParameter("utm_medium"),
                utmCampaign: getUTMParameter("utm_campaign"),
              });
              viewedBannersRef?.current?.add(0);
            }
            // }
            const onSelect = () => {
              const selectedIndex = selected;
              // If the banner at the current index has not been viewed
              if (selected !== 0 && !viewedBannersRef.current?.has(selectedIndex)) {
                viewedBannersRef.current?.add(selectedIndex);
                const currentBanner = carouselData[selectedIndex];
                if (currentBanner) {
                  addDataLayer({
                    event: "banner_view",
                    click_text: currentBanner?.imagealt || "default",
                    click_url: `${selectedIndex + 1}`,
                    page_title: document.title,
                  });

                  sendSalesforceInteraction({
                    name: "homepage_view",
                    banner: process.env.NEXT_PUBLIC_MERCHANT_IDENTIFIER,
                    isAnonymous: Uid ? 0 : 1,
                    gigyaUid: Uid,
                    text: currentBanner?.imagealt ? currentBanner?.imagealt : "default",
                    section: "banner",
                    utmSource: getUTMParameter("utm_source"),
                    utmMedium: getUTMParameter("utm_medium"),
                    utmCampaign: getUTMParameter("utm_campaign"),
                  });
                }
              }
            }
            onSelect()
          }
        }
      } catch (error) {
        console.error("Error in EmblaSlider:", error);
      }
    }
    // console.log("isLoaded isLoaded  isLoaded ==================>:", isLoaded);
  }, [selected, carouselData, bannerKey, isLoaded, isloading]);

  return (
    <section
      className={`slider_wrapper ${className} ${wrapperClassName}`.trim()}
    >
      <div className={`slider_row ${rowClassName}`.trim()}>
        <div className={`slider_viewport relative ${viewportClassName}`.trim()}>

          <div className="relative">
            {isCenterArrow && (
              <div className={`absolute left-0 top-0 h-full w-[150px] z-[2] flex items-center justify-start bg-blackgradientleft ${CenterArrowClassName}`}>
                <Arrow
                  direction="prev"
                  onClick={handlePrevClick}
                  disabled={isAtStart && !options.loop}
                  className={`ml-6 border-[--brandWhite] bg-[--brandWhite] ${arrowClassName} ${leftArrowClassName}`.trim()}
                />
              </div>
            )}

            <Splide
              ref={splideRef}
              options={splideOptions}
              className={`slider_container ${containerClassName}`.trim()}
              onMoved={onMove}
              onMounted={onMove}
            >
              {slides}
            </Splide>
            {isCenterArrow && (
              <div className={`absolute right-0 top-0 h-full w-[150px] z-[2] flex items-center justify-end bg-blackgradientright ${CenterArrowClassName}`}>
                <Arrow
                  direction="next"
                  onClick={handleNextClick}
                  disabled={isAtEnd && !options.loop}
                  className={`mr-6 border-[--brandWhite] bg-[--brandWhite] ${arrowClassName} ${rightArrowClassName}`.trim()}
                />
              </div>
            )}
          </div>

          {/* bottom arrow +  */}
          {shouldShowControls && (
            <div
              className={`slider_controls flex justify-center mt-6 ${controlsClassName}`.trim()}
            >
              <div
                className={`slider_buttons flex items-center gap-[1.5rem] py-[2px] ${buttonsClassName}`.trim()}
              >
                {isShowArrow && (
                  <Arrow
                    direction="prev"
                    onClick={handlePrevClick}
                    disabled={isAtStart && !options.loop}
                    className={`border-[--brandSecondary] bg-[--brandWhite] ${arrowClassName} ${leftArrowClassName}`.trim()}
                  />
                )}
                {slideCount > 1 && (
                  <Pagination
                    slideCount={slideCount}
                    slidesToScroll={currentSlidesToScroll}
                    selected={Math.floor(selected / currentSlidesToScroll)}
                    onClick={(slideIndex) =>
                      splideRef.current?.splide.go(slideIndex)
                    }
                    className={paginationClassName}
                  />
                )}
                {isShowArrow && (
                  <Arrow
                    direction="next"
                    onClick={handleNextClick}
                    disabled={isAtEnd && !options.loop}
                    className={`border-[--brandSecondary] bg-[--brandWhite] ${arrowClassName} ${rightArrowClassName}`.trim()}
                  />
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </section>
  );
};

export default SplideSlider;


export function OffersCarousalSkeleton({ className }: any) {
  return (
    <div
      className={`flex gap-6 w-full overflow-hidden ${className ? className : ""}`}
    >
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          className={`flex flex-col rounded-2xl overflow-hidden shadow animate-pulse bg-white w-full max-w-[220px] shrink-0`}
        >
          <div className="h-6 bg-gray-300 rounded-t-2xl"></div>
          <div className="h-4 bg-gray-300 rounded w-[40%] mt-3 mx-3"></div>

          <div className="relative p-4">
            <div className="pt-[56.25%] bg-gray-300"></div>
            <div className="absolute top-0 right-2 h-10 w-10 bg-gray-300 rounded-full"></div>
            <div className="absolute bottom-0 right-2 h-10 w-20 bg-gray-300 rounded-lg"></div>
          </div>

          <div className="flex flex-col gap-2 p-4 bg-[#f4f4f4] mt-3">
            <div className="h-4 bg-gray-300 rounded w-full"></div>
            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            <div className="h-4 bg-gray-300 rounded w-5/6"></div>
          </div>

          <div className="flex justify-center items-center py-2 bg-gray-100 border-t border-gray-300">
            <div className="mx-6 h-5 w-full bg-gray-300 rounded-full"></div>
          </div>
        </div>
      ))}
    </div>
  );
}